import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Trophy, ArrowLeft, AlertTriangle, Info, TrendingUp, ClipboardList, Star,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal, formatBulan } from "@/lib/tanggal";
import {
  PageHeader, TidakPunyaAkses, adaField, Field, inputCls,
  PLATFORM_LABEL, DELIVERABLE_JENIS_LABEL, fmtPersen,
} from "./_shared";
import { GradeBadge } from "./KolScoreboard";

const COMPONENT_ORDER = ["efisiensi_cpv", "leads_closing", "margin_roi", "profesionalisme"];
const COMPONENT_META = {
  efisiensi_cpv: { label: "Efisiensi CPV", desc: "Biaya per-view (CPV) KOL dibanding median CPV seluruh KOL berdata cukup — makin murah dari median, makin tinggi skornya." },
  leads_closing: { label: "Leads & Closing", desc: "Rasio closing dari leads yang teratribusi lewat campaign yang sama dipakai deal KOL ini." },
  margin_roi: { label: "ROI Margin", desc: "Margin penjualan teratribusi dibanding nilai kontrak KOL — impas (ROI 0%) = skor 50." },
  profesionalisme: { label: "Profesionalisme", desc: "Rata-rata persentase deliverable tepat waktu dan rating internal dari admin." },
};

/**
 * Rincian Skor KOL — acceptance criteria Fase 3: "KOL Score dapat dijelaskan".
 * Setiap komponen WAJIB menampilkan nilai mentah, pembanding (median), bobot,
 * dan skor parsial apa adanya dari backend — tidak dihitung ulang di sini
 * sama sekali (lihat larangan di `_shared.jsx: adaField` soal margin).
 */
export default function KolScoreDetail() {
  const { kolId } = useParams();
  const { can } = useAuth();
  const bolehLihat = can("marketing_kol", "view");
  const bolehUpdate = can("marketing_kol", "update");

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "kol-score-detail", kolId],
    () => api.get(`/marketing/kol-score/${kolId}`).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const { data: kol, loading: loadingKol } = useDataQuery(
    ["marketing", "kol-satu", kolId],
    () => api.get(`/marketing/kol/${kolId}`).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const { data: history, loading: loadingHistory } = useDataQuery(
    ["marketing", "kol-score-history", kolId],
    () => api.get(`/marketing/kol-score/${kolId}/history`).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const { data: deliverablesAll, loading: loadingDeliv, refetch: refetchDeliv } = useDataQuery(
    ["marketing", "kol-deliverables-semua"],
    () => api.get("/marketing/kol/deliverables").then((r) => r.data),
    { enabled: bolehLihat },
  );

  const deliverablesKol = useMemo(
    () => (deliverablesAll || []).filter((dl) => dl.kol_id === kolId)
      .sort((a, b) => (b.due_date || "").localeCompare(a.due_date || "")),
    [deliverablesAll, kolId],
  );

  const chartData = useMemo(
    () => (history?.riwayat || []).map((r) => ({ periode: formatBulan(r.periode), score: r.score })),
    [history],
  );

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Trophy} title="Rincian Skor KOL" />
        <TidakPunyaAkses label="Skor KOL" testid="mkt-kolscoredet-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-kolscore-detail">
      <Link to="/marketing/kol-score" data-testid="mkt-kolscoredet-kembali"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Kembali ke papan peringkat
      </Link>

      <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" data-testid="mkt-kolscoredet-loading">
        {data && (
          <>
            {/* Header identitas + ringkasan skor */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-mono text-xs text-slate-400">{kol?.code}</p>
                  <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-0.5 inline-flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-emerald-600" /> {data.kol_nama || "-"}
                  </h1>
                  {!loadingKol && kol && (
                    <p className="text-sm text-slate-600 mt-1">{PLATFORM_LABEL[kol.platform] || kol.platform}{kol.kategori ? ` · ${kol.kategori}` : ""}</p>
                  )}
                  {data.periode && (
                    <p className="text-xs text-slate-400 mt-1">Periode penilaian: {data.periode.awal} s/d {data.periode.akhir}</p>
                  )}
                </div>
                <div className="text-right">
                  {data.score == null ? (
                    <p className="text-sm text-slate-400">Belum ada data untuk dinilai</p>
                  ) : (
                    <p className={`text-3xl font-bold font-mono ${data.insufficient_data ? "text-slate-400" : "text-slate-900"}`}>{data.score.toFixed(1)}</p>
                  )}
                  <div className="mt-1"><GradeBadge grade={data.grade} insufficientData={data.insufficient_data} reason={data.insufficient_data_reason} /></div>
                </div>
              </div>
              {data.insufficient_data_reason && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 inline-flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {data.insufficient_data_reason}
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100">
                <MiniStat label="Jumlah Deal" value={fmtNum(data.jumlah_deal)} />
                <MiniStat label="Total Spend" value={idr(data.total_spend)} />
                <MiniStat label="Bobot Tersedia" value={`${data.total_bobot_tersedia}/100`} />
                {adaField(data, "median_cpv_populasi") && data.median_cpv_populasi != null && (
                  <MiniStat label="Median CPV Populasi" value={`${idr(data.median_cpv_populasi)}/view`} />
                )}
              </div>
            </div>

            {/* Saran watchlist — SARAN, bukan aksi otomatis (brief eksplisit) */}
            {data.watchlist?.watchlist_disarankan && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-2.5" data-testid="mkt-kolscoredet-watchlist">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <p className="text-sm text-rose-800">{data.watchlist.pesan}</p>
              </div>
            )}

            {/* Rincian komponen */}
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-2">Rincian Komponen Skor</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {COMPONENT_ORDER.filter((k) => adaField(data.components || {}, k)).map((k) => (
                  <ComponentCard key={k} compKey={k} comp={data.components[k]} />
                ))}
              </div>
              {!adaField(data.components || {}, "margin_roi") && (
                <p className="text-[11px] text-slate-400 mt-2">
                  Komponen ROI Margin tidak ditampilkan — Anda tidak punya izin <span className="font-mono">marketing_margin</span>. Skor total di atas dihitung ulang tanpa komponen ini.
                </p>
              )}
            </div>

            {/* Riwayat tren bulanan */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-kolscoredet-history">
              <p className="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-600" /> Tren Skor Bulanan</p>
              <LoadingState loading={loadingHistory} varian="kartu" jumlahKartu={1} kosong={!loadingHistory && chartData.length === 0}
                            pesanKosong="Belum ada riwayat skor bulanan tersimpan." data-testid="mkt-kolscoredet-history-state">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 6, right: 6, left: -8, bottom: 6 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="periode" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => (v == null ? "Belum cukup data" : v.toFixed(1))} contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                      <Line type="monotone" dataKey="score" name="Skor" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </LoadingState>
            </div>

            {/* Rating internal admin */}
            <RatingForm kolId={kolId} currentRating={data.components?.profesionalisme?.internal_rating}
                        disabled={!bolehUpdate} onSaved={refetch} />

            {/* Input performa deliverable — sumber data efisiensi CPV & separuh profesionalisme */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-kolscoredet-deliverables">
              <div className="p-4 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><ClipboardList className="w-4 h-4 text-emerald-600" /> Performa Deliverable</p>
                <p className="text-xs text-slate-500 mt-0.5">Isi jumlah views/engagement tiap deliverable yang sudah tayang — dipakai untuk hitung efisiensi CPV.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr className="text-left">
                      <th className="px-3 py-2.5 font-semibold">Deliverable</th>
                      <th className="px-3 py-2.5 font-semibold">Tenggat</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Views</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Engagement</th>
                      {bolehUpdate && <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <LoadingState loading={loadingDeliv} kosong={!loadingDeliv && deliverablesKol.length === 0}
                                  varian="tabel" kolom={bolehUpdate ? 5 : 4} pesanKosong="Belum ada deliverable untuk KOL ini."
                                  data-testid="mkt-kolscoredet-deliv-state">
                      {deliverablesKol.map((dl) => (
                        <DeliverableRow key={dl.id} dl={dl} bolehUpdate={bolehUpdate} onSaved={() => { refetchDeliv(); refetch(); }} />
                      ))}
                    </LoadingState>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </LoadingState>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-sm font-bold text-slate-900 font-mono">{value}</p>
    </div>
  );
}

function ComponentCard({ compKey, comp }) {
  const meta = COMPONENT_META[compKey] || { label: compKey, desc: "" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid={`mkt-kolscoredet-comp-${compKey}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
          <p className="text-[11px] text-slate-400 mt-0.5 max-w-xs">{meta.desc}</p>
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium whitespace-nowrap shrink-0">Bobot {comp.bobot}%</span>
      </div>
      <div className="flex items-end justify-between mt-3">
        <div>
          {comp.data_cukup ? (
            <p className="text-2xl font-bold font-mono text-slate-900">{comp.skor_parsial}</p>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-500">
              <Info className="w-3 h-3" /> Data belum cukup
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2 border-t border-slate-100 pt-2">{comp.catatan}</p>

      {/* Rincian nilai mentah — beda field per komponen */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-600">
        {compKey === "efisiensi_cpv" && (
          <>
            <RawItem label="CPV KOL" value={comp.cpv_kol != null ? `${idr(comp.cpv_kol)}/view` : "-"} />
            <RawItem label="Median Populasi" value={comp.median_cpv_populasi != null ? `${idr(comp.median_cpv_populasi)}/view` : "-"} />
            <RawItem label="Total Views" value={fmtNum(comp.total_views)} />
            <RawItem label="Total Spend" value={idr(comp.total_spend)} />
          </>
        )}
        {compKey === "leads_closing" && (
          <>
            <RawItem label="Jumlah Lead" value={comp.leads_count ?? "-"} />
            <RawItem label="Jumlah Closing" value={comp.closing_count ?? "-"} />
            <RawItem label="Closing Rate" value={comp.closing_rate != null ? fmtPersen((comp.closing_rate * 100).toFixed(1)) : "-"} />
          </>
        )}
        {compKey === "margin_roi" && (
          <>
            <RawItem label="Margin Teratribusi" value={comp.margin != null ? idr(comp.margin) : "-"} />
            <RawItem label="Nilai Kontrak" value={idr(comp.spend)} />
            <RawItem label="ROI" value={comp.roi != null ? fmtPersen((comp.roi * 100).toFixed(1)) : "-"} />
          </>
        )}
        {compKey === "profesionalisme" && (
          <>
            <RawItem label="On-Time Deliverable" value={comp.on_time_pct != null ? fmtPersen(comp.on_time_pct) : "-"} />
            <RawItem label="Deliverable Dinilai" value={comp.jumlah_deliverable_dinilai ?? 0} />
            <RawItem label="Rating Internal" value={comp.internal_rating != null ? comp.internal_rating : "-"} />
          </>
        )}
      </div>
    </div>
  );
}

function RawItem({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-400">{label}</span>
      <span className="font-mono font-medium text-slate-700">{value}</span>
    </div>
  );
}

/** Form rating internal admin (0-100) — separuh komponen profesionalisme. */
function RatingForm({ kolId, currentRating, disabled, onSaved }) {
  const [rating, setRating] = useState(currentRating != null ? String(currentRating) : "");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);

  const simpan = async () => {
    const nilai = Number(rating);
    if (rating === "" || Number.isNaN(nilai) || nilai < 0 || nilai > 100) {
      toast.error("Rating harus angka 0-100");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/marketing/kol-score/${kolId}/rating`, { internal_rating: nilai, catatan });
      toast.success("Rating internal tersimpan");
      setCatatan("");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan rating internal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="mkt-kolscoredet-rating">
      <p className="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-emerald-600" /> Rating Internal Admin</p>
      <p className="text-xs text-slate-500 mb-3">Penilaian subjektif admin (0-100) — separuh komponen Profesionalisme, digabung persentase deliverable tepat waktu.</p>
      {disabled ? (
        <p className="text-xs text-slate-400">Rating saat ini: <span className="font-mono font-semibold text-slate-600">{currentRating ?? "belum diisi"}</span> — Anda tidak punya izin mengubahnya.</p>
      ) : (
        <div className="flex items-end gap-3 flex-wrap">
          <Field label="Rating (0-100)">
            <input type="number" min="0" max="100" value={rating} onChange={(e) => setRating(e.target.value)}
                   data-testid="mkt-kolscoredet-rating-input" className={`${inputCls} w-28`} />
          </Field>
          <div className="flex-1 min-w-[200px]">
            <Field label="Catatan (opsional)">
              <input value={catatan} onChange={(e) => setCatatan(e.target.value)}
                     data-testid="mkt-kolscoredet-rating-catatan" className={inputCls} placeholder="cth. Responsif, revisi cepat" />
            </Field>
          </div>
          <button onClick={simpan} disabled={busy} data-testid="mkt-kolscoredet-rating-simpan"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : "Simpan Rating"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Baris performa satu deliverable — SET (bukan tambah), merepresentasikan
 *  angka terbaru yang dicek admin dari platform (lihat docstring backend). */
function DeliverableRow({ dl, bolehUpdate, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [views, setViews] = useState(dl.views ?? "");
  const [engagement, setEngagement] = useState(dl.engagement ?? "");
  const [busy, setBusy] = useState(false);

  const simpan = async () => {
    if (views === "" || Number(views) < 0) { toast.error("Views wajib diisi angka >= 0"); return; }
    setBusy(true);
    try {
      await api.patch(`/marketing/kol-score/deliverable/${dl.id}/performance`, {
        views: Number(views), engagement: engagement === "" ? null : Number(engagement),
      });
      toast.success("Performa deliverable tersimpan");
      setEdit(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan performa deliverable"); }
    finally { setBusy(false); }
  };

  return (
    <tr data-testid={`mkt-kolscoredet-deliv-row-${dl.id}`}>
      <td className="px-3 py-2.5">
        <p className="font-medium text-slate-900">{DELIVERABLE_JENIS_LABEL[dl.jenis] || dl.jenis}</p>
        {dl.deskripsi && <p className="text-xs text-slate-500">{dl.deskripsi}</p>}
      </td>
      <td className="px-3 py-2.5 text-slate-600">{formatTanggal(dl.due_date)}</td>
      <td className="px-3 py-2.5 text-right font-mono">
        {edit
          ? <input type="number" min="0" value={views} onChange={(e) => setViews(e.target.value)}
                   data-testid={`mkt-kolscoredet-deliv-views-${dl.id}`} className={`${inputCls} h-8 w-24 text-right ml-auto`} />
          : (dl.views ?? "-")}
      </td>
      <td className="px-3 py-2.5 text-right font-mono">
        {edit
          ? <input type="number" min="0" value={engagement} onChange={(e) => setEngagement(e.target.value)}
                   data-testid={`mkt-kolscoredet-deliv-engagement-${dl.id}`} className={`${inputCls} h-8 w-24 text-right ml-auto`} />
          : (dl.engagement ?? "-")}
      </td>
      {bolehUpdate && (
        <td className="px-3 py-2.5 text-right">
          {edit ? (
            <div className="flex items-center justify-end gap-1.5">
              <button onClick={() => setEdit(false)} className="h-7 px-2 rounded border border-slate-200 text-[11px] text-slate-600">Batal</button>
              <button onClick={simpan} disabled={busy} data-testid={`mkt-kolscoredet-deliv-simpan-${dl.id}`}
                      className="h-7 px-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-medium disabled:opacity-50">
                {busy ? "..." : "Simpan"}
              </button>
            </div>
          ) : (
            <button onClick={() => setEdit(true)} data-testid={`mkt-kolscoredet-deliv-edit-${dl.id}`}
                    className="h-7 px-2 rounded border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 font-medium">
              Isi Performa
            </button>
          )}
        </td>
      )}
    </tr>
  );
}
